import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  TruckIcon,
  CubeIcon,
  BuildingStorefrontIcon,
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  CheckBadgeIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

const ProcurementGuideNew = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('requisitions');

  const pages = [
    { name: 'Help Center', to: '/help', component: Link, current: false },
    { name: 'Procurement Guide', to: '/help/procurement-guide', component: 'span', current: true },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', icon: 'text-blue-600' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800', icon: 'text-purple-600' },
      green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800', icon: 'text-green-600' },
      yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800', icon: 'text-yellow-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800', icon: 'text-orange-600' },
      red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800', icon: 'text-red-600' },
      gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-800', icon: 'text-gray-600' },
      indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800', icon: 'text-indigo-600' },
    };
    return colors[color] || colors.gray;
  };

  const faqs = [
    {
      category: 'Requisition Questions',
      questions: [
        {
          q: 'Do I always need to create a Purchase Order?',
          a: 'No! PO is optional. Use it when: (1) Supplier requires formal documentation, (2) Purchase is expensive (>₦20,000), (3) Company policy requires it, (4) You want quality inspection (GRN). For quick market purchases, skip the PO.'
        },
        {
          q: "What's the difference between a Requisition and a Purchase Order?",
          a: 'Requisition = Internal request ("We need to buy this"). Purchase Order = External document sent to supplier ("We officially order this"). Think of it like: Requisition = Shopping list, Purchase Order = Order form given to shop.'
        },
        {
          q: 'Can I edit a requisition after submitting?',
          a: 'Once submitted, you cannot edit. If you made a mistake: Contact your manager, they can reject the requisition, then you create a new correct one. To avoid this: Double-check before clicking "Submit".'
        },
        {
          q: 'What if the price changed when I went to buy?',
          a: 'That\'s normal! When marking as "Collected": (1) Edit each item, (2) Enter actual price paid, (3) System will use actual price for inventory. The estimate is just a guide.'
        }
      ]
    },
    {
      category: 'Purchase Order Questions',
      questions: [
        {
          q: 'Can I cancel a PO after sending to supplier?',
          a: 'Yes, if not yet delivered: (1) Open the PO, (2) Click "Cancel PO" button, (3) Contact supplier to inform them. Status changes to "Cancelled".'
        },
        {
          q: "Supplier said they didn't receive the PO. What do I do?",
          a: '(1) Go to Purchase Orders, (2) Open the PO, (3) Download PDF again, (4) Resend to supplier, (5) Make sure status is "Sent" in system.'
        },
        {
          q: 'Can I create a PO without a requisition?',
          a: 'No. The workflow is: (1) First create requisition, (2) Get approval, (3) Then generate PO from approved requisition. This ensures proper approval process.'
        }
      ]
    },
    {
      category: 'GRN & Delivery Questions',
      questions: [
        {
          q: 'Do I need to create GRN for every delivery?',
          a: 'No, only for: (1) Purchases with PO, (2) Items that need quality inspection, (3) Expensive or critical items. Quick purchases don\'t need GRN.'
        },
        {
          q: 'What if supplier delivered MORE items than ordered?',
          a: 'In GRN: Ordered Quantity: (from PO), Received Quantity: (actual count - higher number), Rejected: 0, Accepted: (the actual amount). System will accept the extra items. But check with manager if this is okay.'
        },
        {
          q: 'Can I accept some items and reject others from same delivery?',
          a: 'Yes! That\'s the point of GRN. For each item: Accept what\'s good, Reject what\'s bad, System tracks both separately.'
        },
        {
          q: 'What happens to rejected items?',
          a: 'They DON\'T go into stock. They\'re documented in GRN. You should: (1) Keep them separate (don\'t mix with good items), (2) Label them "REJECTED - GRN-XXX", (3) Wait for supplier to collect OR return to supplier.'
        }
      ]
    },
    {
      category: 'Inventory Questions',
      questions: [
        {
          q: 'How do I check what\'s in stock?',
          a: '(1) Go to Procurement → Inventory, (2) Click Stock tab, (3) Search for item by name or part number, (4) Current quantity shows.'
        },
        {
          q: 'Can I manually add items to inventory?',
          a: 'Not directly. Inventory updates automatically when you: (1) Mark requisition as "Collected", (2) Submit a GRN. This ensures proper tracking.'
        },
        {
          q: 'What if stock quantity is wrong?',
          a: '(1) Go to Inventory → Movement tab, (2) Click "Record Movement", (3) Select movement type: "ADJUSTMENT", (4) Enter correct quantity, (5) Add reason in remarks. This creates audit trail for the correction.'
        },
        {
          q: 'Where do received items go in inventory?',
          a: 'Quick Purchase (Path A): OPEN FLOOR. Standard Purchase (Path B): OPEN FLOOR. Formal Purchase/GRN (Path C): RECEIVING BAY. You can move them to proper location later in Inventory management.'
        }
      ]
    }
  ];

  const statusReference = {
    requisition: [
      { status: 'Pending', meaning: 'Waiting for manager approval', action: 'Wait for review', color: 'yellow' },
      { status: 'Approved', meaning: 'Ready to purchase', action: 'Buy items OR generate PO', color: 'blue' },
      { status: 'Collected', meaning: 'Items purchased and received', action: 'Complete', color: 'green' },
      { status: 'Rejected', meaning: 'Manager denied request', action: 'Review reason, resubmit', color: 'red' },
    ],
    po: [
      { status: 'Draft', meaning: 'PO created, not sent', action: 'Review and send', color: 'gray' },
      { status: 'Sent', meaning: 'Sent to supplier', action: 'Await delivery', color: 'blue' },
      { status: 'Acknowledged', meaning: 'Supplier confirmed', action: 'Await delivery', color: 'purple' },
      { status: 'Partially Received', meaning: 'Some items delivered', action: 'Await remaining', color: 'yellow' },
      { status: 'Completed', meaning: 'All items received', action: 'Complete', color: 'green' },
      { status: 'Cancelled', meaning: 'Order cancelled', action: 'Create new if needed', color: 'red' },
    ],
    grn: [
      { status: 'Fully Received', meaning: 'All items received and accepted', color: 'green' },
      { status: 'Partially Received', meaning: 'Received less than ordered', color: 'yellow' },
      { status: 'Discrepancy', meaning: 'Different amount but accepted', color: 'orange' },
      { status: 'Quality Issue', meaning: 'Items rejected', color: 'red' },
    ]
  };

  const quickActions = [
    { name: 'Create Requisition', path: '/procurement/buyItem', icon: ShoppingCartIcon, color: 'blue', description: 'Start a new purchase request' },
    { name: 'Purchase Orders', path: '/procurement/purchase-orders', icon: DocumentTextIcon, color: 'purple', description: 'View and manage POs' },
    { name: 'Goods Received', path: '/procurement/grn', icon: ClipboardDocumentCheckIcon, color: 'green', description: 'Inspect deliveries' },
    { name: 'Inventory', path: '/procurement/inventory', icon: CubeIcon, color: 'orange', description: 'Check stock levels' },
  ];

  const procurementPaths = [
    {
      path: 'A',
      name: 'Quick Purchase',
      subtitle: 'Informal - No Paperwork',
      icon: ShoppingCartIcon,
      color: 'blue',
      time: '30 min - 2 hours',
      whenToUse: ['Urgent small purchases', 'Local market buying', 'Price negotiated on-site', 'No registered supplier', 'Value < ₦5,000'],
      steps: ['Create Requisition', 'Get Approval', 'Buy Items', 'Mark Collected', 'Stock Updated'],
    },
    {
      path: 'B',
      name: 'Standard Purchase',
      subtitle: 'Optional PO',
      icon: DocumentTextIcon,
      color: 'purple',
      time: '1-3 days',
      whenToUse: ['Registered supplier', 'Known price', 'Non-critical items', 'No quality concerns', 'Standard parts'],
      steps: ['Create Requisition', 'Get Approval', 'Generate PO (Optional)', 'Items Delivered', 'Mark Collected', 'Stock Updated'],
    },
    {
      path: 'C',
      name: 'Formal Purchase',
      subtitle: 'Full Documentation + QC',
      icon: ClipboardDocumentCheckIcon,
      color: 'green',
      time: '3-7 days',
      whenToUse: ['Expensive items (>₦20,000)', 'Critical safety parts', 'Bulk orders', 'Quality-sensitive items', 'Supplier quality history'],
      steps: ['Create Requisition', 'Get Approval', 'Generate PO', 'Send to Supplier', 'Items Delivered', 'Create GRN', 'Quality Inspection', 'Stock Updated'],
    },
  ];

  return (
    <>
      <BreadCrumb pages={pages} />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-lg p-8 mb-8 text-white">
          <div className="flex items-center mb-4">
            <ShoppingCartIcon className="h-12 w-12 mr-4" />
            <div>
              <h1 className="text-4xl font-bold">Procurement Guide</h1>
              <p className="text-xl text-purple-100">Complete Guide for Purchasing & Inventory Management</p>
            </div>
          </div>
          <p className="text-purple-100 mt-2">
            Learn how to create requisitions, manage purchase orders, receive deliveries, and track inventory.
          </p>
          <div className="mt-4 flex items-center text-purple-200 text-sm">
            <ClockIcon className="h-4 w-4 mr-1" />
            Last Updated: January 2026
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const colors = getColorClasses(action.color);
              return (
                <Link
                  key={action.name}
                  to={action.path}
                  className={`flex items-center p-4 ${colors.bg} hover:opacity-80 rounded-lg border-2 ${colors.border} transition-all hover:shadow-md`}
                >
                  <Icon className={`h-10 w-10 ${colors.icon} mr-3`} />
                  <div>
                    <h3 className={`font-semibold ${colors.text}`}>{action.name}</h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Document Flow Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <ArrowPathIcon className="h-7 w-7 text-purple-600 mr-2" />
            Procurement Flow Overview
          </h2>

          <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
              <div className="flex-shrink-0 bg-blue-100 rounded-lg px-4 py-3 text-center border-2 border-blue-300">
                <DocumentDuplicateIcon className="h-8 w-8 text-blue-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-blue-900">Requisition</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-yellow-100 rounded-lg px-4 py-3 text-center border-2 border-yellow-300">
                <CheckBadgeIcon className="h-8 w-8 text-yellow-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-yellow-900">Approval</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-purple-100 rounded-lg px-4 py-3 text-center border-2 border-purple-300 relative">
                <DocumentTextIcon className="h-8 w-8 text-purple-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-purple-900">PO</span>
                <span className="absolute -top-2 -right-2 bg-yellow-400 text-xs px-1 rounded">Optional</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-indigo-100 rounded-lg px-4 py-3 text-center border-2 border-indigo-300">
                <TruckIcon className="h-8 w-8 text-indigo-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-indigo-900">Delivery</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-green-100 rounded-lg px-4 py-3 text-center border-2 border-green-300">
                <CubeIcon className="h-8 w-8 text-green-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-green-900">Inventory</span>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-4 text-sm">
              Create request → Get approval → (Optional: Generate PO) → Receive items → Update stock
            </p>
          </div>
        </div>

        {/* Three Paths Decision Tool */}
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg shadow-md p-6 mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-7 w-7 text-yellow-600 mr-2" />
            Quick Decision: Which Path Should I Use?
          </h2>

          <div className="space-y-3">
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="font-semibold text-gray-900 mb-2">Is this urgent and cheap (&lt;₦5,000)?</p>
              <div className="ml-4 flex items-center gap-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-1" />
                  <span className="text-green-700 font-medium">YES → Path A (Quick)</span>
                </div>
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-gray-400 mr-1" />
                  <span className="text-gray-600">NO → Continue...</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <p className="font-semibold text-gray-900 mb-2">Is this from a registered supplier with no quality concerns?</p>
              <div className="ml-4 flex items-center gap-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-1" />
                  <span className="text-purple-700 font-medium">YES → Path B (Standard)</span>
                </div>
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-gray-400 mr-1" />
                  <span className="text-gray-600">NO → Continue...</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <p className="font-semibold text-gray-900 mb-2">Is this expensive (&gt;₦20,000) or quality-critical?</p>
              <div className="ml-4 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-1" />
                <span className="text-green-700 font-medium">YES → Path C (Formal with GRN)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Three Procurement Paths */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">The Three Procurement Paths</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {procurementPaths.map((path) => {
              const colors = getColorClasses(path.color);
              const Icon = path.icon;
              return (
                <div key={path.path} className={`${colors.bg} border-2 ${colors.border} rounded-lg shadow-lg overflow-hidden`}>
                  <div className={`p-4 ${path.color === 'blue' ? 'bg-blue-600' : path.color === 'purple' ? 'bg-purple-600' : 'bg-green-600'} text-white`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-bold">Path {path.path}</span>
                      <Icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-bold">{path.name}</h3>
                    <p className="text-sm opacity-90">{path.subtitle}</p>
                    <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium bg-white bg-opacity-20`}>
                      ⏱ {path.time}
                    </span>
                  </div>

                  <div className="p-4">
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">When to Use:</h4>
                      <ul className="space-y-1">
                        {path.whenToUse.map((item, idx) => (
                          <li key={idx} className="text-xs text-gray-700 flex items-start">
                            <CheckCircleIcon className={`h-3 w-3 ${colors.icon} mr-1 mt-0.5 flex-shrink-0`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">Steps:</h4>
                      <div className="space-y-1">
                        {path.steps.map((step, idx) => (
                          <div key={idx} className="flex items-center text-xs">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${colors.badge} font-semibold mr-2 flex-shrink-0`}>
                              {idx + 1}
                            </span>
                            <span className="text-gray-700">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8 overflow-hidden">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px min-w-max">
              {[
                { id: 'requisitions', name: 'Requisitions', icon: DocumentDuplicateIcon },
                { id: 'purchase-orders', name: 'Purchase Orders', icon: DocumentTextIcon },
                { id: 'grn', name: 'GRN / Receiving', icon: ClipboardDocumentCheckIcon },
                { id: 'inventory', name: 'Inventory', icon: CubeIcon },
                { id: 'suppliers', name: 'Suppliers', icon: BuildingStorefrontIcon },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600 bg-purple-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } flex-1 whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center justify-center transition-colors min-w-[140px]`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Requisitions Tab */}
            {activeTab === 'requisitions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <DocumentDuplicateIcon className="h-6 w-6 text-blue-600 mr-2" />
                    Creating a Purchase Requisition
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-3">Step-by-Step Process</h4>
                      <ol className="space-y-3">
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                          <div>
                            <p className="font-medium text-gray-900">Navigate to Procurement → Buy Item</p>
                            <p className="text-sm text-gray-600">Click "Make Purchase Request"</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                          <div>
                            <p className="font-medium text-gray-900">Select Job Card (Optional)</p>
                            <p className="text-sm text-gray-600">Link to specific job or leave for general purchase</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                          <div>
                            <p className="font-medium text-gray-900">Add Items</p>
                            <p className="text-sm text-gray-600">Part name, quantity, estimated price</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                          <div>
                            <p className="font-medium text-gray-900">Select Supplier (Optional)</p>
                            <p className="text-sm text-gray-600">Choose from registered suppliers or leave blank</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">5</span>
                          <div>
                            <p className="font-medium text-gray-900">Submit for Approval</p>
                            <p className="text-sm text-gray-600">Awaits manager review</p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                        <h4 className="font-semibold text-purple-900 mb-3">Requisition Status</h4>
                        <div className="space-y-2">
                          {statusReference.requisition.map((item, idx) => {
                            const colors = getColorClasses(item.color);
                            return (
                              <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${colors.badge}`}>{item.status}</span>
                                <span className="text-xs text-gray-600">{item.meaning}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                        <h4 className="font-semibold text-green-900 mb-3">After Approval</h4>
                        <ul className="text-sm text-gray-700 space-y-2">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                            <span>Go buy items OR generate PO</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                            <span>Update actual prices when collecting</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                            <span>Mark as "Collected" to update stock</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Purchase Orders Tab */}
            {activeTab === 'purchase-orders' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-2" />
                    Managing Purchase Orders
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-3">Creating a Purchase Order</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">1</span>
                          <span className="text-gray-700">Go to approved requisition</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">2</span>
                          <span className="text-gray-700">Click "Generate PO"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">3</span>
                          <span className="text-gray-700">Review items and supplier details</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">4</span>
                          <span className="text-gray-700">Confirm and create PO</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">5</span>
                          <span className="text-gray-700">Download PDF and send to supplier</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-indigo-50 rounded-lg p-5 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3">PO Status Reference</h4>
                      <div className="space-y-2">
                        {statusReference.po.map((item, idx) => {
                          const colors = getColorClasses(item.color);
                          return (
                            <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border text-xs">
                              <span className={`px-2 py-1 rounded font-medium ${colors.badge}`}>{item.status}</span>
                              <span className="text-gray-600">{item.action}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mt-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-yellow-800">When to Use Purchase Orders</h4>
                        <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                          <li>Supplier requires formal documentation</li>
                          <li>Purchase exceeds ₦20,000</li>
                          <li>You need quality inspection (GRN)</li>
                          <li>Company policy requires it</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* GRN Tab */}
            {activeTab === 'grn' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-600 mr-2" />
                    Goods Received Note (GRN)
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-3">Creating a GRN</h4>
                      <ol className="space-y-3">
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                          <div>
                            <p className="font-medium text-gray-900">Go to Procurement → GRN</p>
                            <p className="text-sm text-gray-600">Click "Create New GRN"</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                          <div>
                            <p className="font-medium text-gray-900">Select Purchase Order</p>
                            <p className="text-sm text-gray-600">Items auto-populate from PO</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                          <div>
                            <p className="font-medium text-gray-900">Inspect Each Item</p>
                            <p className="text-sm text-gray-600">Enter received, accepted, rejected quantities</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                          <div>
                            <p className="font-medium text-gray-900">Add Photos (Optional)</p>
                            <p className="text-sm text-gray-600">Document any damage or issues</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">5</span>
                          <div>
                            <p className="font-medium text-gray-900">Submit GRN</p>
                            <p className="text-sm text-gray-600">Accepted items go to Receiving Bay</p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                        <h4 className="font-semibold text-orange-900 mb-3">GRN Status</h4>
                        <div className="space-y-2">
                          {statusReference.grn.map((item, idx) => {
                            const colors = getColorClasses(item.color);
                            return (
                              <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${colors.badge}`}>{item.status}</span>
                                <span className="text-xs text-gray-600">{item.meaning}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                          <XCircleIcon className="h-5 w-5 mr-2" />
                          Handling Rejected Items
                        </h4>
                        <ul className="text-sm text-red-700 space-y-1">
                          <li>• Don't mix with accepted items</li>
                          <li>• Label as "REJECTED - GRN-XXX"</li>
                          <li>• Store separately</li>
                          <li>• Contact supplier for return/replacement</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CubeIcon className="h-6 w-6 text-orange-600 mr-2" />
                    Inventory Management
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-3">Checking Stock</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">1</span>
                          <span className="text-gray-700">Go to Procurement → Inventory</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">2</span>
                          <span className="text-gray-700">Click Stock tab</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">3</span>
                          <span className="text-gray-700">Search by item name or part number</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-orange-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">4</span>
                          <span className="text-gray-700">View quantity by location</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-3">Stock Locations</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex items-center">
                            <ArchiveBoxIcon className="h-5 w-5 text-blue-600 mr-2" />
                            <span className="text-sm font-medium">Open Floor</span>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Quick/Standard purchases</span>
                        </div>
                        <div className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex items-center">
                            <ArchiveBoxIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Receiving Bay</span>
                          </div>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">GRN deliveries</span>
                        </div>
                        <div className="flex items-center justify-between bg-white p-3 rounded border">
                          <div className="flex items-center">
                            <ArchiveBoxIcon className="h-5 w-5 text-purple-600 mr-2" />
                            <span className="text-sm font-medium">Main Store</span>
                          </div>
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Organized storage</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mt-4">
                    <div className="flex">
                      <InformationCircleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Stock Adjustments</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          If stock quantity is wrong: Go to Movement tab → Record Movement → Select "ADJUSTMENT" → Enter correct quantity with reason. This creates an audit trail.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Suppliers Tab */}
            {activeTab === 'suppliers' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <BuildingStorefrontIcon className="h-6 w-6 text-indigo-600 mr-2" />
                    Supplier Management
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-indigo-50 rounded-lg p-5 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3">Registering a Supplier</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">1</span>
                          <span className="text-gray-700">Go to Procurement → Suppliers</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">2</span>
                          <span className="text-gray-700">Click "Add New Supplier"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">3</span>
                          <span className="text-gray-700">Enter company name, contact, address</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">4</span>
                          <span className="text-gray-700">Add payment terms and bank details</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">5</span>
                          <span className="text-gray-700">Save supplier</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-3">Benefits of Registered Suppliers</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                          <span className="text-gray-700">Quick selection in requisitions</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                          <span className="text-gray-700">Auto-fill PO details</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                          <span className="text-gray-700">Track purchase history</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                          <span className="text-gray-700">Evaluate supplier performance</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                          <span className="text-gray-700">Manage payment terms</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <InformationCircleIcon className="h-7 w-7 text-purple-600 mr-2" />
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((category, categoryIdx) => (
              <div key={categoryIdx}>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{category.category}</h3>
                <div className="space-y-2">
                  {category.questions.map((faq, faqIdx) => {
                    const globalIdx = `${categoryIdx}-${faqIdx}`;
                    const isOpen = openFaq === globalIdx;
                    return (
                      <div key={faqIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleFaq(globalIdx)}
                          className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="p-4 bg-white border-t border-gray-200">
                            <p className="text-gray-700 whitespace-pre-line">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Checklist */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg shadow-md p-6 mb-8 border-2 border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Daily Procurement Checklist</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">AM</span>
                Start of Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review pending requisitions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check expected deliveries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review low stock alerts</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-yellow-900 mb-3 flex items-center">
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mr-2">ONGOING</span>
                Throughout Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Process new requisitions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Receive and inspect deliveries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Update PO statuses</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded mr-2">PM</span>
                End of Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Complete all GRNs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Mark collected items</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Follow up on overdue POs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-8 border-2 border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Pro Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-blue-900 mb-2">For Workshop Staff</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Be specific in descriptions (e.g., "Brake pads for Toyota Corolla 2015, Front")</li>
                <li>• Include job card number in notes</li>
                <li>• Check stock before requesting</li>
                <li>• Group related items in one requisition</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-purple-900 mb-2">For Procurement Staff</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Compare prices from 2-3 suppliers</li>
                <li>• Check quality before buying</li>
                <li>• Keep original packaging</li>
                <li>• Update actual prices promptly</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-green-900 mb-2">For Receiving Staff</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Inspect items immediately upon delivery</li>
                <li>• Count everything - don't assume</li>
                <li>• Take clear photos of any damage</li>
                <li>• Complete GRN same day</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-orange-900 mb-2">For Managers</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Review requisitions thoroughly</li>
                <li>• Check if item already in stock</li>
                <li>• Question vague descriptions</li>
                <li>• Prioritize urgent requests</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-gray-100 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Company Details for Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <p><strong>Company:</strong> AYDENS Garage</p>
              <p><strong>Address:</strong> Block 7, Plot 7, Etal Avenue, Off Kudirat Abiola Way, Oregun, Lagos, Nigeria</p>
            </div>
            <div>
              <p><strong>Phone:</strong> +234-7045972414</p>
              <p><strong>Email:</strong> info@aydensgarage.com</p>
              <p><strong>TIN:</strong> 313422177-001 | <strong>RC:</strong> 697295</p>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link
            to="/help"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            ← Back to Help Center
          </Link>
          <div className="flex gap-3">
            <Link
              to="/procurement/buyItem"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Create Requisition
            </Link>
            <Link
              to="/procurement/manage"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              Manage Procurement
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProcurementGuideNew;
